<template>
  <div class="row">
    <div class="col-xs-12">
      <div class="ibox">
        <div class="ibox-title">
          <div class="row">
            <div class="col-xs-12">
              <h1>Weight Tracking Settings</h1>
            </div>
          </div>
        </div>
        <div class="ibox-content">
          <div class="grid grid-cols-2 sm-grid-cols-1 gap-x-xl gap-y-lg">
            <div>
              <p class="font-semi m-b-m">
                Do you want wrestlers to log their weight at the
                <a :href="kioskHelpUrl" target="_blank" rel="noopener" class="text-underline">kiosk</a>? Turning on this setting makes it
                so:
              </p>
              <ul class="weight-tracking-benefits">
                <li class="flex gap-x-sm">
                  <i class="fa-solid fa-check text-green-600" aria-hidden="true"></i>
                  <span>
                    Wrestlers enter their weight before a kiosk check-in finishes. A repeat scan
                    for the same practice doesn't ask again.
                  </span>
                </li>
                <li class="flex gap-x-sm">
                  <i class="fa-solid fa-check text-green-600" aria-hidden="true"></i>
                  <span>
                    A Log weight link on the
                    <a :href="kioskHelpUrl" target="_blank" rel="noopener" class="text-underline">kiosk</a> lets wrestlers record extra
                    weigh-ins without checking in, like after a hard practice.
                  </span>
                </li>
                <li class="flex gap-x-sm">
                  <i class="fa-solid fa-check text-green-600" aria-hidden="true"></i>
                  <span>
                    Every wrestler's profile gets a Weight tab with their full history. Coaches can
                    add, fix, or remove entries there.
                  </span>
                </li>
                <li class="flex gap-x-sm">
                  <i class="fa-solid fa-check text-green-600" aria-hidden="true"></i>
                  <span>
                    The check-in report gains a Weight at Check-In column.
                  </span>
                </li>
              </ul>
              <p class="weight-tracking-note text-gray-600">
                <small>
                  <b>Note:</b> wrestlers and their parents can see their own weight history but
                  can't change it. Only coaches can edit or delete entries.
                </small>
              </p>
              <form role="form" id="weight-tracking-form" @submit.prevent="update">
                <div class="form-group m-t-lg">
                  <label class="font-semi">Should wrestlers log their weight at the kiosk?</label>
                  <div class="m-t-sm">
                    <label class="flex">
                      <input
                        type="radio"
                        name="weight_tracking_enabled"
                        :value="true"
                        v-model="enabled"
                      />
                      <div class="m-l-m flex flex-col">
                        <span class="font-semi">Yes</span>
                        <p class="font-normal m-b-none">
                          Wrestlers are asked for their weight when they check in, except on
                          the rosters listed below
                        </p>
                      </div>
                    </label>
                    <label class="flex m-t-m">
                      <input
                        type="radio"
                        name="weight_tracking_enabled"
                        :value="false"
                        v-model="enabled"
                      />
                      <div class="m-l-m flex flex-col">
                        <span class="font-semi">No</span>
                        <p class="font-normal m-b-none">
                          No weigh-ins at the kiosk, and the Weight tab is hidden. Past weigh-ins
                          are kept if you turn it back on.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
                <div class="form-group m-t-lg">
                  <label class="font-semi">Rosters that skip the weigh-in</label>
                  <p class="m-t-none m-b-sm">
                    Turn weight tracking off for a roster on that roster's edit page. A wrestler
                    skips the weigh-in only when every active roster they're on has it turned off.
                    Wrestlers who aren't on any roster are always asked.
                  </p>
                  <ul v-if="optedOutRosters.length" class="weight-tracking-rosters">
                    <li
                      v-for="roster in optedOutRosters"
                      :key="roster.id"
                      class="flex align-items-center justify-space-between gap-x-sm"
                    >
                      <span class="font-semi">{{ roster.name }}</span>
                      <a :href="roster.edit_path" class="text-underline">Edit roster</a>
                    </li>
                  </ul>
                  <p v-else class="text-gray-600 m-b-none">
                    None. Every roster is asked for a weight.
                  </p>
                </div>
                <div class="m-t-lg flex align-items-center gap-x-xs">
                  <ladda-button
                    @lbClicked="update"
                    el-class="btn btn-primary"
                    :loading="updating"
                  >
                    Save Changes
                  </ladda-button>
                  <a :href="settingsPath" class="btn btn-default">Cancel</a>
                  <small v-if="error" class="text-danger m-l-sm">{{ errorMessage }}</small>
                </div>
              </form>
            </div>
            <div class="pause-preview-well">
              <p class="upper-label m-t-none m-b-xxs">What wrestlers see</p>
              <template v-if="enabled">
                <p class="m-b-m">
                  A live example of the
                  <a :href="kioskHelpUrl" target="_blank" rel="noopener" class="text-underline">kiosk</a> weight prompt, in your team
                  colors.
                </p>
                <div class="m-b-m">
                  <div class="btn-group" role="group" aria-label="Kiosk screen">
                    <button
                      v-for="mode in previewModes"
                      :key="mode.key"
                      type="button"
                      class="btn btn-default btn-sm"
                      :class="{ active: previewMode === mode.key }"
                      @click="previewMode = mode.key"
                    >
                      {{ mode.label }}
                    </button>
                  </div>
                  <p class="text-gray-600 m-t-xs m-b-none">
                    <small>{{ currentPreviewMode.caption }}</small>
                  </p>
                </div>
                <div class="weight-preview" :style="brandStyle">
                  <div class="weight-preview__modal">
                    <span class="weight-preview__icon">
                      <!-- Same Font Awesome Pro "weight" (light) icon as KioskWeightModal.vue -->
                      <svg viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
                        <path d="M448 64h-64.81C353.95 25.38 308.07 0 256 0s-97.95 25.38-127.19 64H64C28.71 64 0 92.71 0 128v320c0 35.29 28.71 64 64 64h384c35.29 0 64-28.71 64-64V128c0-35.29-28.71-64-64-64zM256 32c70.69 0 128 57.31 128 128s-57.31 128-128 128-128-57.31-128-128S185.31 32 256 32zm224 416c0 17.67-14.33 32-32 32H64c-17.67 0-32-14.33-32-32V128c0-17.67 14.33-32 32-32h45.56C100.94 115.63 96 137.22 96 160c0 88.22 71.78 160 160 160s160-71.78 160-160c0-22.78-4.94-44.37-13.56-64H448c17.67 0 32 14.33 32 32v320zM256 256c26.47 0 48-21.53 48-48 0-13.92-6.05-26.36-15.54-35.13l30.24-70.57c3.48-8.11-.28-17.52-8.41-21-8.08-3.48-17.52.27-21 8.41l-30.26 70.6C223.57 158 208 188.65 208 208c0 26.47 21.53 48 48 48zm0-64c8.84 0 16 7.16 16 16s-7.16 16-16 16-16-7.16-16-16 7.16-16 16-16z"/>
                      </svg>
                    </span>
                    <div class="weight-preview__text">
                      <h3 class="weight-preview__title">
                        {{ previewMode === 'standalone' ? 'Log weight' : 'Log weight to check in' }}
                      </h3>
                      <p class="weight-preview__name">{{ sampleWrestler.name }}</p>
                      <p class="weight-preview__prompt">
                        {{ previewMode === 'standalone'
                          ? 'Enter your weight.'
                          : 'Enter your weight to finish checking in.' }}
                      </p>
                      <p class="weight-preview__last">
                        Last logged: {{ sampleWrestler.lastWeight }} lbs
                      </p>
                    </div>
                    <div class="weight-preview__when">
                      <span class="weight-preview__chip">
                        <i class="fa-solid fa-calendar-days" aria-hidden="true"></i> Today
                      </span>
                      <span class="weight-preview__chip">
                        <i class="fa-regular fa-clock" aria-hidden="true"></i> {{ currentTime }}
                      </span>
                    </div>
                    <div class="weight-preview__field">
                      <div class="weight-preview__input">
                        <span class="weight-preview__input-label">
                          Weight ({{ previewUnits === 'lbs' ? 'lb' : 'kg' }})
                        </span>
                        <span class="weight-preview__input-value">{{ sampleWeight }}</span>
                      </div>
                      <div class="weight-preview__units">
                        <button
                          v-for="unit in ['lbs', 'kg']"
                          :key="unit"
                          type="button"
                          class="weight-preview__unit"
                          :class="{ 'is-selected': unit === previewUnits }"
                          @click="previewUnits = unit"
                        >
                          {{ unit }}
                        </button>
                      </div>
                    </div>
                    <div class="weight-preview__actions">
                      <span class="weight-preview__confirm">Confirm</span>
                      <span class="weight-preview__cancel">Cancel</span>
                    </div>
                  </div>
                </div>
                <p class="text-gray-600 m-t-sm m-b-none">
                  <small>
                    Wrestlers can switch between lbs and kg, and each kiosk remembers the last
                    choice. Try the toggle above.
                  </small>
                </p>
              </template>
              <p v-else class="text-gray-600 m-b-none">
                Wrestlers won't be asked for a weight at the kiosk until you turn weight tracking
                on.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { errorableMix } from './mixins/errorable_mix';

// Admin settings for kiosk weight tracking, with a mock of the kiosk weigh-in prompt
// (app/packs/kiosk/components/KioskWeightModal.vue). The kiosk pack is Tailwind, so the mock is
// rebuilt with check_ins.scss classes and takes the team's brand colors through the same
// --team-primary / --team-text variables the kiosk uses. Keep the copy in sync with that modal.
const PREVIEW_MODES = [
  {
    key: 'check-in',
    label: 'At check-in',
    caption: 'Shown after a wrestler picks a practice, before the check-in is saved.',
  },
  {
    key: 'standalone',
    label: 'Log weight link',
    caption: 'Shown from the Log weight link next to a wrestler\'s last weight, for a weigh-in without checking in.',
  },
];

const SAMPLE_WRESTLER = { name: 'Alex Rivera', lastWeight: 142.6 };

const KIOSK_HELP_URL = 'https://help.wrestlingiq.com/article/149-using-the-wrestlingiq-kiosk';

export default {
  name: 'weight-tracking-settings',
  components: { LaddaButton },
  mixins: [errorableMix],
  props: {
    teamId: {
      type: Number,
      required: true,
    },
    initialEnabled: {
      type: Boolean,
      default: false,
    },
    // [{ id, name, edit_path }] active rosters with weight_tracking_disabled
    optedOutRosters: {
      type: Array,
      default() {
        return [];
      },
    },
    // Team#brand_bg_color / #brand_text_color, mirroring the kiosk's branding
    brandColor: {
      type: String,
      default: '#27AA83',
    },
    brandTextColor: {
      type: String,
      default: '#FFFFFF',
    },
    settingsPath: {
      type: String,
      default: '/settings/team?tab=kiosk',
    },
  },
  data() {
    return {
      enabled: this.initialEnabled,
      kioskHelpUrl: KIOSK_HELP_URL,
      previewModes: PREVIEW_MODES,
      previewMode: PREVIEW_MODES[0].key,
      previewUnits: 'lbs',
      sampleWrestler: SAMPLE_WRESTLER,
      currentTime: moment().format('h:mm a'),
      updating: false,
    };
  },
  computed: {
    currentPreviewMode() {
      return _.find(this.previewModes, { key: this.previewMode });
    },
    brandStyle() {
      return {
        '--team-primary': this.brandColor,
        '--team-text': this.brandTextColor,
      };
    },
    sampleWeight() {
      const lbs = SAMPLE_WRESTLER.lastWeight + 0.8;
      return this.previewUnits === 'kg' ? (lbs * 0.4536).toFixed(1) : lbs.toFixed(1);
    },
  },
  methods: {
    update: _.throttle(function () {
      if (this.updating) return;

      const vm = this;
      vm.updating = true;
      vm.error = false;
      const url = vm.$apiService.teamUrl(vm.teamId);
      const data = {
        team: {
          weight_tracking_enabled: vm.enabled,
        },
      };

      axios
        .put(url, data)
        .then(() => {
          vm.$notificationManager.emit('show-toast', 'Weight tracking settings saved', true);
          vm.updating = false;
        })
        .catch((error) => {
          vm.updating = false;
          vm.errorMessage = `Error: ${error.toString()}`;
          vm.error = true;
        });
    }, 500),
  },
};
</script>
